// Ported from dev-scrips-pm-react src/shared/lib/analytics.ts (PROD-1119 documents module).
/**
 * Thin analytics façade — fire-and-forget event tracker.
 *
 * **PROD-861**: Mixpanel SDK now wired via `@app/integrations/mixpanel`.
 * The Mixpanel integration's auth-boot effect calls `mixpanel.init(token)`
 * once `VITE_MIXPANEL_TOKEN` is present + a user exists. This facade
 * forwards every `track()` call through. If init hasn't run yet (token
 * missing, or no auth user, or a track happens too early in the boot
 * sequence), we fall back to the dev-only `console.debug` of the
 * previous stub so the wiring stays observable.
 *
 * Call sites under `src/reports/` are unchanged — they still call
 * `track('event', props)` and the implementation now routes through
 * Mixpanel automatically.
 *
 * **PHI rule (non-negotiable):** `props` MUST contain filter / column
 * KEYS only (e.g. `Object.keys(filterList)`), never values. A patient
 * name, MRN, DOB or diagnosis in `props` would leak through Mixpanel
 * and violate HIPAA.
 *
 * **PHI rule — extends to `reportName`:** Reports in Scrips are
 * admin-defined templates (e.g. "Active Patients", "Revenue per
 * Practitioner"), so the name string is structurally safe today. If a
 * future report category ever introduces dynamic / user-derived names
 * (e.g. a patient-specific report whose title contains the patient
 * name), strip the identifying portion before passing `reportName`
 * into `track`. Same discipline as the values rule above — names that
 * contain identity belong nowhere near analytics.
 *
 * **Dev/prod detection:** `import.meta.env.DEV` is replaced at build
 * time by Vite (`true` under `vite dev`, the literal `false` under
 * `vite build`). The AKS-served `dist/` bundle therefore tree-shakes
 * the `console.debug` branch entirely — no runtime check, no console
 * noise in production. No custom env flag is consulted here.
 */

import mixpanel from 'mixpanel-browser';

export function track(
  event: string,
  props: Record<string, unknown> = {},
): void {
  try {
    // mixpanel-browser exposes `__loaded` once init has run. When the
    // app is configured without a token (dev with no `VITE_MIXPANEL_TOKEN`),
    // the integration short-circuits init and __loaded stays false — we
    // log to console.debug in dev so the wiring is observable.
    const initialized =
      typeof (mixpanel as unknown as { __loaded?: boolean }).__loaded ===
      'boolean'
        ? (mixpanel as unknown as { __loaded: boolean }).__loaded
        : false;

    if (initialized) {
      mixpanel.track(event, props);
      return;
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- intentional dev observability
      console.debug('[analytics]', event, props);
    }
  } catch {
    // Analytics must never propagate failures upstream.
  }
}
